import type { BanquetteRail, BanquetteAdditionalPanel } from '@estimator/shared';

/** The two panel lists every framing config carries. */
export type PanelListKey = 'rails' | 'additional_panels';

/**
 * Structural shape shared by BanquetteFraming, BackCushionFraming,
 * SeatCushionFraming and ArmFraming. Both arrays are optional here because
 * framing JSON persisted before a list existed comes back without the key.
 */
export interface PanelFraming {
  rails?: BanquetteRail[];
  additional_panels?: BanquetteAdditionalPanel[];
}

/** Total panels across both lists — the count shown on the paste button. */
export function countPanels(framing: PanelFraming): number {
  return (framing.rails?.length ?? 0) + (framing.additional_panels?.length ?? 0);
}

/**
 * Stamp one material onto every panel in both lists. Overwrites materials
 * already assigned; names, dimensions, qty_per_section and finish state are
 * left exactly as they were.
 */
export function applyMaterialToAll<F extends PanelFraming>(framing: F, materialId: string): F {
  return {
    ...framing,
    rails: (framing.rails ?? []).map((r) => ({ ...r, material_id: materialId })),
    additional_panels: (framing.additional_panels ?? []).map((p) => ({
      ...p,
      material_id: materialId,
    })),
    // Spreading a generic widens the result, so the cast restores F. The
    // shape is unchanged — only material_id values differ.
  } as F;
}

/**
 * Reorder one list, leaving the other exactly as it was. Out-of-range or
 * no-op moves return the same reference, so a drag that lands where it
 * started does not mark the editor dirty.
 */
export function movePanel<F extends PanelFraming>(
  framing: F,
  list: PanelListKey,
  from: number,
  to: number,
): F {
  const items = framing[list];
  if (!items) return framing;
  if (from === to) return framing;
  if (from < 0 || to < 0 || from >= items.length || to >= items.length) return framing;
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return { ...framing, [list]: next } as F;
}
